import { useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { MobileFrame } from "@/components/MobileFrame";
import { Swatch, RubberStamp, type SwatchKind } from "@/components/primitives";
import { useMyProfile } from "@/hooks/useFigData";
import { scanStore } from "@/lib/scanStore";
import { toast } from "@/lib/toast";

interface ShareState {
  scanId: string | null;
  photoUrl: string | null;
  title: string;
  brand: string;
  era: string;
  confidence: number;
  priceLow: number | null;
  priceHigh: number | null;
  priceSuggested: number | null;
}

/** Format a UUID into a human-readable FIG scan reference (FIG-XXXXXXXX). */
function formatScanId(id: string | null): string {
  if (!id) return "";
  return `FIG-${id.replace(/-/g, "").slice(0, 8).toUpperCase()}`;
}

/** Best price string from low/high/suggested. Returns "—" when nothing usable. */
function formatValue(
  low: number | null,
  high: number | null,
  suggested: number | null,
): string {
  if (low && high) return `$${low}–${high}`;
  if (suggested) return `$${suggested}`;
  return "—";
}

export default function Share() {
  const nav = useNavigate();
  const { state } = useLocation() as { state: ShareState | null };
  const { profile } = useMyProfile();
  const cardRef = useRef<HTMLDivElement | null>(null);
  const [capturing, setCapturing] = useState(false);

  // Data resolution: prefer the explicit state passed via nav("/share", { state })
  // from the Result page. When state is absent (e.g. a deep-link, or the
  // "Share your profile" entry from Settings), fall back to the most-recent
  // verify result + photo set held in scanStore. This way the card renders
  // with real numbers instead of placeholder FIG-?????? / 0%.
  const verify = state ? null : scanStore.getVerifyResult();
  const storedPhotos = state ? [] : scanStore.getPhotos();

  const scanId = state?.scanId ?? null;
  const photoUrl = state?.photoUrl ?? storedPhotos[0] ?? null;
  const title = state?.title || verify?.item?.detected_type || "";
  const brand = state?.brand || verify?.item?.brand_detected || "";
  const era = state?.era || verify?.item?.estimated_era || "";

  const valueDisplay = formatValue(
    state?.priceLow ?? null,
    state?.priceHigh ?? null,
    state?.priceSuggested ?? null,
  );
  const figId = formatScanId(scanId);
  const certUrl = figId
    ? `figverified.com/${figId.toLowerCase()}`
    : "figverified.com";

  // ─── User identity for the "Filed by" row ──────────────────────────────────
  const handle = profile?.handle ?? "you";
  const displayName = profile?.display_name ?? handle;
  const avatarKind: SwatchKind = (profile?.avatar ?? "olive") as SwatchKind;

  // ─── Card → image capture for Instagram / TikTok ───────────────────────────
  // Targets the cardRef div specifically so the captured image is just the
  // certificate, not the surrounding nav / share buttons.
  const captureCard = async (): Promise<Blob | null> => {
    if (!cardRef.current) return null;
    setCapturing(true);
    try {
      const html2canvas = (await import("html2canvas")).default;
      const canvas = await html2canvas(cardRef.current, {
        backgroundColor: null, // preserve the card's own cream background
        scale: 2,              // retina-quality output for social
        useCORS: true,         // allow the Supabase photo URL to load into canvas
        logging: false,
      });
      return await new Promise<Blob | null>((resolve) =>
        canvas.toBlob((b) => resolve(b), "image/png"),
      );
    } catch (err) {
      console.warn("[share] card capture failed", err);
      return null;
    } finally {
      setCapturing(false);
    }
  };

  const handleImageShare = async (label: string) => {
    if (!photoUrl && !title) {
      toast.error("Nothing to share — verify an item first.");
      return;
    }
    const blob = await captureCard();
    if (!blob) {
      toast.error("Couldn't generate share image.");
      return;
    }
    const file = new File(
      [blob],
      `fig-${(scanId ?? "card").slice(0, 8)}.png`,
      { type: "image/png" },
    );
    // Web Share Level 2 — files. iOS Safari + Capacitor WKWebView support this.
    const navAny = navigator as unknown as {
      canShare?: (data: { files: File[] }) => boolean;
      share?: (data: { files?: File[]; title?: string; url?: string }) => Promise<void>;
    };
    if (navAny.canShare?.({ files: [file] })) {
      try {
        await navAny.share?.({
          files: [file],
          title: `${title || "Vintage item"} — FIG Verified`,
        });
        return;
      } catch {
        // user cancelled the system sheet — fall through to download fallback
      }
    }
    // Fallback: trigger a save so the user can pick the image up in IG / TikTok.
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = file.name;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Card saved. Open ${label} and pick it from your photos.`);
  };

  const handleUrlShare = async () => {
    const url = scanId
      ? `${window.location.origin}/cert?scanId=${scanId}`
      : `${window.location.origin}/`;
    const navAny = navigator as unknown as {
      share?: (data: { title?: string; url?: string }) => Promise<void>;
    };
    if (navAny.share) {
      try {
        await navAny.share({
          title: `${title || "Vintage item"} — FIG Verified`,
          url,
        });
        return;
      } catch {
        // user cancelled — fall through to clipboard
      }
    }
    await navigator.clipboard.writeText(url).catch(() => {});
    toast.success("Link copied.");
  };

  const eraBrandLine = [era, brand].filter(Boolean).join(" · ");

  return (
    <MobileFrame>
      <div style={{
        minHeight: "100%",
        background: "hsl(var(--cream))",
        color: "hsl(var(--ink))",
        paddingBottom: 60,
      }}>
        {/* Top bar — close + Share label */}
        <div style={{
          padding: "12px 20px 0",
          display: "flex", justifyContent: "space-between", alignItems: "center",
        }}>
          <button
            onClick={() => nav(-1)}
            aria-label="Close"
            style={{
              background: "none",
              border: "1px solid hsl(var(--ink))",
              padding: 6, cursor: "pointer",
              color: "hsl(var(--ink))",
              fontFamily: "'IBM Plex Mono', monospace", fontSize: 14, lineHeight: 1,
            }}
          >
            ✕
          </button>
          <span style={{
            fontFamily: "'IBM Plex Mono', monospace", fontSize: 10,
            letterSpacing: "0.18em", textTransform: "uppercase",
            color: "hsl(var(--ink-2))",
          }}>
            Share
          </span>
          <div style={{ width: 30 }} />
        </div>

        {/* ── The Card — html2canvas capture target ─────────────────────── */}
        <div style={{ padding: "24px 20px 0" }}>
          <div
            ref={cardRef}
            style={{
              background: "hsl(var(--cream))",
              border: "1px solid hsl(var(--ink) / 0.18)",
              borderRadius: 16,
              boxShadow:
                "0 1px 0 hsl(var(--ink) / 0.04), 0 12px 28px -18px hsl(var(--ink) / 0.25)",
              padding: "20px 18px 22px",
              color: "hsl(var(--ink))",
              overflow: "hidden",
            }}
          >
            {/* 1. FIG wordmark — small, top-center */}
            <div style={{ textAlign: "center", marginBottom: 14 }}>
              <span style={{
                fontFamily: "'Fraunces', 'Playfair Display', serif",
                fontWeight: 900, fontStyle: "italic", fontSize: 32,
                letterSpacing: "-0.05em", lineHeight: 1,
                color: "hsl(var(--ink))",
                fontVariationSettings:
                  '"opsz" 144, "SOFT" 100, "WONK" 1',
              }}>
                fig
              </span>
            </div>

            {/* 2. Item photo — skipped gracefully when missing */}
            {photoUrl && (
              <div style={{
                width: "100%", aspectRatio: "4/3",
                borderRadius: 12, overflow: "hidden",
                marginBottom: 14, background: "hsl(var(--paper-3))",
              }}>
                <img
                  src={photoUrl}
                  alt={title || "Item photo"}
                  crossOrigin="anonymous"
                  style={{
                    width: "100%", height: "100%",
                    objectFit: "cover", display: "block",
                  }}
                />
              </div>
            )}

            {/* 3. Era · Brand line */}
            {eraBrandLine && (
              <div style={{
                fontFamily: "'Inter', sans-serif",
                fontWeight: 500, fontSize: 11,
                letterSpacing: "0.14em", textTransform: "uppercase",
                color: "hsl(var(--rust))",
                marginBottom: 6,
              }}>
                {eraBrandLine}
              </div>
            )}

            {/* 4. Item title */}
            {title && (
              <div style={{
                fontFamily: "'Fraunces', 'Playfair Display', serif",
                fontWeight: 700, fontSize: 24, lineHeight: 1.1,
                color: "hsl(var(--ink))",
                marginBottom: 16,
              }}>
                {title}
              </div>
            )}

            {/* 5. Scores row — Value Estimate. Confidence was previously
                surfaced here as a percent, but it's an internal/private
                signal we don't want printed on a publicly-shared card. */}
            <div style={{
              display: "flex", alignItems: "flex-start",
              borderTop: "1px solid hsl(var(--ink) / 0.18)",
              borderBottom: "1px solid hsl(var(--ink) / 0.18)",
              padding: "12px 0", margin: "0 0 16px",
            }}>
              <div style={{ flex: 1 }}>
                <div style={{
                  fontFamily: "'Inter', sans-serif",
                  fontWeight: 600, fontSize: 10,
                  letterSpacing: "0.16em", textTransform: "uppercase",
                  color: "hsl(var(--ink-2))",
                }}>
                  Value Estimate
                </div>
                <div style={{
                  fontFamily: "'Fraunces', 'Playfair Display', serif",
                  fontWeight: 700, fontSize: 26, lineHeight: 1.1,
                  color: "hsl(var(--rust))",
                  marginTop: 2,
                }}>
                  {valueDisplay}
                </div>
              </div>
            </div>

            {/* 6. FIG Certified badge — existing circular RubberStamp */}
            <div style={{
              display: "flex", justifyContent: "center", marginBottom: 12,
            }}>
              <RubberStamp
                label="CERTIFIED"
                sub="FIG VERIFIED"
                color="hsl(var(--rust))"
                size={104}
              />
            </div>

            {/* 7. Verified-by line */}
            <div style={{
              textAlign: "center",
              fontFamily: "'Inter', sans-serif",
              fontWeight: 500, fontSize: 10,
              letterSpacing: "0.1em",
              color: "hsl(var(--ink-2))",
              marginBottom: 14,
            }}>
              Authenticated by FIG · {certUrl}
            </div>

            {/* 8. Filed-by row — avatar + display name + @handle */}
            <div style={{
              display: "flex", alignItems: "center", gap: 10,
              borderTop: "1px dashed hsl(var(--ink) / 0.18)",
              paddingTop: 12,
            }}>
              <Swatch
                kind={avatarKind}
                style={{
                  width: 28, height: 28, borderRadius: "50%",
                  border: "1px solid hsl(var(--ink))",
                  flexShrink: 0,
                }}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontFamily: "'Inter', sans-serif",
                  fontWeight: 600, fontSize: 12,
                  color: "hsl(var(--ink))",
                  whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                }}>
                  Filed by {displayName}
                </div>
                <div style={{
                  fontFamily: "'Inter', sans-serif",
                  fontWeight: 500, fontSize: 10,
                  color: "hsl(var(--ink-2))",
                  marginTop: 1,
                }}>
                  @{handle}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Share buttons — Instagram, TikTok, Text, Copy ─────────────── */}
        <div style={{
          padding: "24px 20px 0",
          display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 10,
        }}>
          {([
            { label: "Instagram", onClick: () => handleImageShare("Instagram") },
            { label: "TikTok",    onClick: () => handleImageShare("TikTok") },
            { label: "Text",      onClick: handleUrlShare },
            { label: "Copy",      onClick: handleUrlShare },
          ] as const).map((o) => (
            <button
              key={o.label}
              type="button"
              onClick={o.onClick}
              disabled={capturing}
              style={{
                padding: "12px 8px",
                background: "hsl(var(--cream))",
                color: "hsl(var(--ink))",
                border: "1.5px solid hsl(var(--ink))",
                borderRadius: 14,
                fontFamily: "'Inter', sans-serif",
                fontWeight: 600, fontSize: 12,
                cursor: capturing ? "wait" : "pointer",
                opacity: capturing ? 0.6 : 1,
              }}
            >
              {o.label}
            </button>
          ))}
        </div>
      </div>
    </MobileFrame>
  );
}
